import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { getUser, getPropertyConfig } from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Eye, Check, Trash2, RotateCcw, Edit } from 'lucide-react';
import { toast } from 'sonner';

export default function StoreRequisitions() {
  const [srs, setSRs] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedSR, setSelectedSR] = useState(null);
  const user = getUser();
  const propConfig = getPropertyConfig();
  const hasPO = propConfig?.has_po !== false;
  const srLabel = hasPO ? 'Store Requisition' : 'Barang Keluar';

  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const canCreate = userRoles.some(r => ['Admin', 'StoreRequisition'].includes(r));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [srsRes, wareRes] = await Promise.all([
        api.get('/store-requisitions'),
        api.get('/warehouses')
      ]);
      setSRs(srsRes.data);
      setWarehouses(wareRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async (srNumber) => {
    if (!window.confirm('Release this SR? Stock will be deducted.')) return;
    
    try {
      await api.patch(`/store-requisitions/${srNumber}/release`);
      toast.success('SR released successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release SR');
    }
  };

  const handleDelete = async (srNumber) => {
    if (!window.confirm('Are you sure you want to delete this Store Requisition?')) return;
    
    try {
      await api.delete(`/store-requisitions/${srNumber}`);
      toast.success('Store Requisition deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleReopen = async (srNumber) => {
    if (!window.confirm('Reopen this SR? Status will be changed to Open and stock will be restored.')) return;
    
    try {
      await api.patch(`/store-requisitions/${srNumber}/reopen`);
      toast.success('Store Requisition reopened successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen');
    }
  };

  const handleEdit = (sr) => {
    setSelectedSR(sr);
    setEditDialogOpen(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="store-requisitions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">{srLabel}</h1>
          <p className="text-muted-foreground">{hasPO ? 'Manage item withdrawals' : 'Catat barang keluar dari gudang'}</p>
        </div>
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-sr-button">
                <Plus className="h-4 w-4 mr-2" />
                {hasPO ? 'Create SR' : 'Barang Keluar Baru'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{hasPO ? 'Create Store Requisition' : 'Barang Keluar Baru'}</DialogTitle>
              </DialogHeader>
              <SRForm 
                warehouses={warehouses}
                onSuccess={() => {
                  setDialogOpen(false);
                  fetchData();
                }} 
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="sr-table">
            <thead>
              <tr className="border-b bg-secondary/50">
                <th className="text-left p-3 font-semibold">SR Number</th>
                <th className="text-left p-3 font-semibold">Date</th>
                <th className="text-left p-3 font-semibold">Location</th>
                <th className="text-left p-3 font-semibold">Made By</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-center p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {srs.map((sr) => (
                <tr key={sr.sr_number} className="border-b hover:bg-accent/20 transition-colors">
                  <td className="p-3 font-medium">{sr.sr_number}</td>
                  <td className="p-3">{new Date(sr.date).toLocaleDateString()}</td>
                  <td className="p-3">{sr.location}</td>
                  <td className="p-3">{sr.made_by}</td>
                  <td className="p-3">
                    <span className={`status-badge status-${sr.status.toLowerCase()}`}>
                      {sr.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedSR(sr);
                          setViewDialogOpen(true);
                        }}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {sr.status === 'Open' && canCreate && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(sr)}
                            data-testid={`edit-sr-${sr.sr_number}`}
                            title="Edit SR"
                          >
                            <Edit className="h-4 w-4 text-primary" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRelease(sr.sr_number)}
                            data-testid={`release-sr-${sr.sr_number}`}
                            title="Release SR"
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(sr.sr_number)}
                            data-testid={`delete-sr-${sr.sr_number}`}
                            title="Delete SR"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                      {sr.status === 'Release' && userRoles.includes('Admin') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleReopen(sr.sr_number)}
                          data-testid={`reopen-sr-${sr.sr_number}`}
                          title="Reopen SR (Admin Only)"
                        >
                          <RotateCcw className="h-4 w-4 text-blue-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Store Requisition Details</DialogTitle>
          </DialogHeader>
          {selectedSR && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SR Number</Label>
                  <p className="font-medium">{selectedSR.sr_number}</p>
                </div>
                <div>
                  <Label>Date</Label>
                  <p className="font-medium">{new Date(selectedSR.date).toLocaleDateString()}</p>
                </div>
                <div>
                  <Label>Document Reference</Label>
                  <p className="font-medium">{selectedSR.document || '-'}</p>
                </div>
                <div>
                  <Label>Location</Label>
                  <p className="font-medium">{selectedSR.location}</p>
                </div>
                <div>
                  <Label>Made By</Label>
                  <p className="font-medium">{selectedSR.made_by}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <p className="font-medium">{selectedSR.status}</p>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Items</Label>
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-secondary/50">
                      <th className="text-left p-2">Item</th>
                      <th className="text-left p-2">Warehouse</th>
                      <th className="text-center p-2">Quantity</th>
                      <th className="text-left p-2">Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSR.items.map((item, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="p-2">{item.item_name}</td>
                        <td className="p-2 text-sm text-muted-foreground">{item.warehouse || selectedSR.location}</td>
                        <td className="p-2 text-center">{item.quantity} {item.unit}</td>
                        <td className="p-2 text-sm text-muted-foreground">{item.remark || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Store Requisition - {selectedSR?.sr_number}</DialogTitle>
          </DialogHeader>
          {selectedSR && (
            <SREditForm 
              sr={selectedSR}
              warehouses={warehouses}
              onSuccess={() => {
                setEditDialogOpen(false);
                setSelectedSR(null);
                fetchData();
              }} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SRForm({ warehouses, onSuccess }) {
  const [formData, setFormData] = useState({
    document: '',
    date: new Date().toISOString().split('T')[0],
    location: '',
    items: []
  });
  const [availableStock, setAvailableStock] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    item_id: '',
    quantity: '',
    remark: ''
  });

  useEffect(() => {
    const fetchStock = async () => {
      try {
        const res = await api.get(`/stock?warehouse=${formData.location}`);
        setAvailableStock(res.data);
      } catch (error) {
        toast.error('Failed to fetch stock');
      }
    };

    if (formData.location) {
      fetchStock();
    }
  }, [formData.location]);

  const addItem = () => {
    if (!currentItem.item_id || !currentItem.quantity) {
      toast.error('Please fill all fields');
      return;
    }

    const stock = availableStock.find(s => s.item_id === currentItem.item_id);
    if (!stock) {
      toast.error('Item not found in stock');
      return;
    }

    if (parseFloat(currentItem.quantity) > stock.quantity) {
      toast.error('Insufficient stock');
      return;
    }

    const newItem = {
      item_id: stock.item_id,
      item_name: stock.item_name,
      unit: stock.unit,
      quantity: parseFloat(currentItem.quantity),
      warehouse: formData.location,
      remark: currentItem.remark || ''
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setCurrentItem({ item_id: '', quantity: '', remark: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      await api.post('/store-requisitions', {
        ...formData,
        date: new Date(formData.date).toISOString()
      });
      toast.success('SR created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create SR');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Document Reference</Label>
          <Input
            value={formData.document}
            onChange={(e) => setFormData({ ...formData, document: e.target.value })}
            required
          />
        </div>
        <div>
          <Label>Date</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />
        </div>
      </div>

      <div>
        <Label>Location (Warehouse)</Label>
        <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })}>
          <SelectTrigger>
            <SelectValue placeholder="Select warehouse" />
          </SelectTrigger>
          <SelectContent>
            {warehouses.map((wh) => (
              <SelectItem key={wh.id} value={wh.id}>
                {wh.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {formData.location && availableStock.length === 0 && (
        <div className="border-t pt-4">
          <p className="text-sm text-muted-foreground text-center py-4 bg-yellow-50 rounded-lg border border-yellow-200 p-3">
            Belum ada stok di gudang ini. Lakukan <strong>Barang Masuk</strong> terlebih dahulu.
          </p>
        </div>
      )}

      {availableStock.length > 0 && (
        <div className="border-t pt-4">
          <Label className="mb-2 block">Add Items</Label>
          <div className="flex gap-2 mb-4 flex-wrap">
            <Select value={currentItem.item_id} onValueChange={(value) => setCurrentItem({ ...currentItem, item_id: value })}>
              <SelectTrigger className="flex-1 min-w-[200px]">
                <SelectValue placeholder="Select item" />
              </SelectTrigger>
              <SelectContent>
                {availableStock.map((stock) => (
                  <SelectItem key={stock.item_id} value={stock.item_id}>
                    {stock.item_name} (Available: {stock.quantity} {stock.unit})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              placeholder="Quantity"
              value={currentItem.quantity}
              onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
              step="0.01"
              className="w-28"
            />
            <Input
              placeholder="Remark (kegunaan)"
              value={currentItem.remark}
              onChange={(e) => setCurrentItem({ ...currentItem, remark: e.target.value })}
              className="w-44"
            />
            <Button type="button" onClick={addItem}>Add</Button>
          </div>

          {formData.items.length > 0 && (
            <table className="w-full">
              <thead>
                <tr className="border-b bg-secondary/50">
                  <th className="text-left p-2">Item</th>
                  <th className="text-left p-2">Warehouse</th>
                  <th className="text-center p-2">Quantity</th>
                  <th className="text-left p-2">Remark</th>
                  <th className="text-center p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {formData.items.map((item, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="p-2">{item.item_name}</td>
                    <td className="p-2 text-sm text-muted-foreground">{item.warehouse}</td>
                    <td className="p-2 text-center">{item.quantity} {item.unit}</td>
                    <td className="p-2 text-sm text-muted-foreground">{item.remark || '-'}</td>
                    <td className="p-2 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            items: prev.items.filter((_, i) => i !== idx)
                          }));
                        }}
                        title="Remove Item"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Button type="submit" className="w-full btn-primary">Create Store Requisition</Button>
    </form>
  );
}

function SREditForm({ sr, warehouses, onSuccess }) {
  const [formData, setFormData] = useState({
    document: sr.document || '',
    date: sr.date ? sr.date.split('T')[0] : new Date().toISOString().split('T')[0],
    location: sr.location || '',
    items: sr.items || []
  });

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: field === 'quantity' ? parseFloat(value) || 0 : value
    };
    setFormData({ ...formData, items: newItems });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, idx) => idx !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      await api.put(`/store-requisitions/${sr.sr_number}`, {
        ...formData,
        date: new Date(formData.date).toISOString()
      });
      toast.success('Store Requisition updated successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update SR');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Document Reference</Label>
          <Input
            value={formData.document}
            onChange={(e) => setFormData({ ...formData, document: e.target.value })}
            required
            className="bg-white border-2 border-primary/30 focus:border-primary"
          />
        </div>
        <div>
          <Label>Date</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            className="bg-white border-2 border-primary/30 focus:border-primary"
          />
        </div>
      </div>

      <div>
        <Label>Location (Warehouse)</Label>
        <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })}>
          <SelectTrigger className="bg-white border-2 border-primary/30">
            <SelectValue placeholder="Select warehouse" />
          </SelectTrigger>
          <SelectContent>
            {warehouses.map((wh) => (
              <SelectItem key={wh.id} value={wh.id}>
                {wh.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border-t pt-4">
        <Label className="mb-2 block">Items</Label>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b bg-secondary/50">
                <th className="text-left p-2">Item</th>
                <th className="text-left p-2">Unit</th>
                <th className="text-center p-2">Quantity</th>
                <th className="text-center p-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item, idx) => (
                <tr key={idx} className="border-b">
                  <td className="p-2">{item.item_name}</td>
                  <td className="p-2">{item.unit}</td>
                  <td className="p-2">
                    <Input
                      type="number"
                      value={item.quantity || ''}
                      onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                      step="0.01"
                      min="0"
                      className="w-24 bg-white border-2 border-primary/30"
                    />
                  </td>
                  <td className="p-2 text-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveItem(idx)}
                      title="Remove Item"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Button type="submit" className="w-full btn-primary">Update Store Requisition</Button>
    </form>
  );
}
